import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { ExtractedPackingListTableItem } from "api/models/documentProcessor";
import { useGetExtractedPackingListTable } from "api/queries/documentProcessor/useGetExtractedPackingListTable";
import { retryExtractedPackingListJob } from "api/services/documentProcessor";
import DataTable from "components/v2/Table/DataTable";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import DocumentInput from "./components/DocumentInput";
import ExtractedPackingListReview from "./components/ExtractedPackingListReview";
import useGetDocumentParserLabels from "./components/labels";
import useGetExtractedPackingListColumns from "./columns";
import "./DocumentParser.scss";

export default function DocumentParser() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ExtractedPackingListTableItem>();

  const requestType = searchParams.get("request_type");
  const { permissions } = usePermissions();
  const { create_quick_requests, create_quote_requests } = JSON.parse(permissions ?? "{}").menu_items;

  useEffect(() => {
    if (!create_quick_requests && !create_quote_requests) {
      navigate("/");
      return;
    }
    if (requestType) return;
    const defaultRequestType = create_quick_requests ? "quickrequest" : "shipment";
    searchParams.set("request_type", defaultRequestType);
    setSearchParams(searchParams);
  }, [requestType, searchParams]);

  const { mutate: retryExtractedPackingListJobMutation, isLoading: retryExtractedPackingListJobLoading } = useMutation({
    mutationFn: (jobId: number) => retryExtractedPackingListJob(jobId),
    onSuccess: () => refetchExtractedPackingListTable(),
    onError: (err) => errorToast(err),
  });

  const handleClickViewDraft = (job: ExtractedPackingListTableItem) => {
    setSelectedJob(job);
    setReviewDrawerOpen(true);
  };

  const handleRetryJob = (jobId: number) => {
    retryExtractedPackingListJobMutation(jobId);
  };

  const handleClickContinue = (job: ExtractedPackingListTableItem) => {
    if (requestType === "quickrequest") navigate(`/quick-request?draft_id=${job.draft_reference_number}`);
    else navigate(`/quote-request?draft_id=${job.draft_reference_number}`);
  };

  const { columns } = useGetExtractedPackingListColumns({
    onClickViewDraft: handleClickViewDraft,
    onClickContinue: handleClickContinue,
    onRetryJob: handleRetryJob,
    retryExtractedPackingListJobLoading,
  });

  const {
    instance,
    loading,
    totalData,
    totalPages,
    currentPage,
    itemsPerPage,
    setItemsPerPage,
    setCurrentPage,
    refetch: refetchExtractedPackingListTable,
    refetchEndpoint: refetchWithoutRefreshing,
  } = useTableQueryRequest(useGetExtractedPackingListTable, columns, {
    params: () => ({
      request_type: requestType,
      page: searchParams.get("page") || "1",
      sort_by: null,
    }),
  });

  const hasOneJobProcessing = instance?.data?.some((job: any) => job.status === "processing");

  useEffect(() => {
    if (!hasOneJobProcessing) return;
    const interval = setInterval(() => {
      refetchWithoutRefreshing();
    }, 10000);
    return () => clearInterval(interval);
  }, [hasOneJobProcessing, refetchWithoutRefreshing]);

  const { PageTitle, PageSubtitle, UploadTitle, UploadTooltips, CustomNoResults } = useGetDocumentParserLabels({
    requestType: requestType || "",
  });

  return (
    <div className="d-flex justify-content-center w-100">
      <div style={{ width: "100%", maxWidth: "1280px" }}>
        <ExtractedPackingListReview
          isOpen={reviewDrawerOpen}
          onClose={() => setReviewDrawerOpen(false)}
          selectedJob={selectedJob}
        />

        <PageTitle />
        <PageSubtitle />

        <div className="bg-primary-alt radius-sm">
          <UploadTitle />

          <div className="py-xl px-3xl d-flex flex-column gap-xl">
            <UploadTooltips />
            <DocumentInput refetchExtractedPackingListTable={refetchExtractedPackingListTable} />
          </div>
        </div>

        <div className="bg-primary-alt mt-3xl p-3xl radius-sm">
          <DataTable
            tableLayout="fixed"
            tableInstance={instance}
            isLoading={loading}
            includeTableFooter={instance?.data?.length > 0}
            customNoResults={<CustomNoResults />}
            footerProps={{
              totalPages,
              totalData,
              currentPage,
              itemsPerPage,
              setItemsPerPage,
              setCurrentPage,
            }}
          />
        </div>
      </div>
    </div>
  );
}
